<template>
  <AnalyticsWidget
    :title="title"
    :subTitle="subTitle"
    :embedded="embedded"
    :gridWidth="gridWidth"
    :gridHeight="gridHeight">
    <div class="h-100">
      <ReportFilterPopover
        headerTitle="Inventory Daily Usage Filter"
        :showApplyButton="true"
        :showResetButton="true"
        :appendToBody="true"
        alignment="left"
        placement="bottom-end"
        @apply="applyStockUsageFilter"
        @reset="resetStockUsageFilter">
        <template v-slot:popover>
          <ReportFilterGroup label="Date Filter">
            <ReportDateRangeSelect
              :startDate="editingStockUsageFilter.startDate"
              :endDate="editingStockUsageFilter.endDate"
              @updateStartDate="editingStockUsageFilter.startDate = $event"
              @updateEndDate="editingStockUsageFilter.endDate = $event"
            />
          </ReportFilterGroup>
          <ReportFilterGroup label="Stock Filter" v-if="stockFilterEnabled">
            <FormInputStockCode
              v-model="editingStockUsageFilter.stockId"
              @updateStockInfo="editingStockUsageFilter.stockInfo = $event"
              placeholder="Stock filter"
              :dropdownAutoWidth="true"
              :appendToBody="true"
              :stockType="InventoryStockTypes.glass" />
          </ReportFilterGroup>
        </template>
        <template v-slot:filterDisplay>
          <div>
            <i class="far fa-calendar mr-2"></i>{{appliedStockUsageFilter.startDate | formatDate}} - {{appliedStockUsageFilter.endDate | formatDate}}
          </div>
          <div v-if="hasStockFilter">
            <i class="far fa-inventory mr-2"></i>{{appliedStockUsageFilter.stockInfo ? appliedStockUsageFilter.stockInfo.name : 'None'}}
          </div>
        </template>
      </ReportFilterPopover>

      <ChartLine
        title="Inventory Value"
        :series="stockUsageData.series"
        :xAxis="stockUsageData.xAxis"
        :yAxis="stockUsageData.yAxis"
        :legend="stockUsageData.legend"
        :options="stockUsageData.options"
        :popupEnabled="true"
        :exportEnabled="true"
      />
    </div>
  </AnalyticsWidget>
</template>


<script>
import _ from 'lodash'
import moment from 'moment'

import AnalyticsWidget from '@/modules/intelligence/components/analyticsWidget'
import AnalyticsDashboardValueRow from '@/modules/intelligence/components/analyticsDashboardValueRow'
import AnalyticsDashboardValue from '@/modules/intelligence/components/analyticsDashboardValue'
import AnalyticsDashboardInfo from '@/modules/intelligence/components/analyticsDashboardInfo'

import ReportFilterPopover from '@/components/reporting/reportFilterPopover'
import ReportDateRangeSelect from '@/components/reporting/reportDateRangeSelect'
import ReportFilterGroup from '@/components/reporting/reportFilterGroup'
import FormInputStockCode from '@/modules/inventory/components/formInputStockCode'

import ChartLine from '@/components/charts/chartLine'
import { InventoryStockTypes } from '@/store/types'

export default {
  name: 'InventoryStockDailyChartWidget',
  components: {
    AnalyticsWidget,
    AnalyticsDashboardValueRow,
    AnalyticsDashboardValue,
    AnalyticsDashboardInfo,
    ChartLine,
    ReportFilterPopover,
    ReportDateRangeSelect,
    ReportFilterGroup,
    FormInputStockCode
  },
  props: {
    title: String,
    subTitle: String,
    gridWidth: Number,
    gridHeight: Number,
    embedded: Boolean,
    stockFilterEnabled: {
      type: Boolean,
      default: true
    },
    stockId: Number
  },
  data() {
    return {
      stockUsageData: {
        series: [],
        xAxis: {},
        yAxis: {},
        legend: {},
        options: {}
      },
      defaultStockUsageFilter: {
        startDate: moment().local().subtract(6, 'months').startOf('day').toDate().toISOString(),
        endDate: moment().local().endOf('day').toDate().toISOString(),
        stockId: this.stockId,
        stockInfo: {}
      },
      editingStockUsageFilter: {},
      appliedStockUsageFilter: {},
    }
  },
  computed: {
    hasStockFilter() {
      return !!this.defaultStockUsageFilter.stockId && this.stockFilterEnabled
    }
  },
  created() {
    this.InventoryStockTypes = InventoryStockTypes
  },
  mounted() {
    this.editingStockUsageFilter = _.clone(this.defaultStockUsageFilter)
    this.appliedStockUsageFilter = _.clone(this.defaultStockUsageFilter)

    this.requestChartData()
  },
  watch: {
    stockId: function() {
      this.defaultStockUsageFilter.stockId = this.stockId
      this.editingStockUsageFilter.stockId = this.stockId
      this.appliedStockUsageFilter.stockId = this.stockId

      this.requestChartData()
    }    
  },
  methods: {
    applyStockUsageFilter() {
      this.appliedStockUsageFilter = _.clone(this.editingStockUsageFilter)

      this.requestChartData()
    },
    resetStockUsageFilter() {
      this.editingStockUsageFilter = _.clone(this.defaultStockUsageFilter)
      this.appliedStockUsageFilter = _.clone(this.defaultStockUsageFilter)

      this.requestChartData()
    },
    requestChartData() {
      var stockUsageFilter = {
        type: 'daily',
        startDate: this.appliedStockUsageFilter.startDate,
        endDate: this.appliedStockUsageFilter.endDate,
        stockId: this.appliedStockUsageFilter.stockId,
      }

      let widgetComp = this

      this.$apiService.analyticsService.getInventoryUsageChartData(stockUsageFilter)
      .then(response => {
        let seriesList = response.data
        var glassSizeList = _.uniqBy(seriesList, 'glassSizeId')

        // Filter out series with no data
        seriesList = seriesList.filter(seriesInfo => seriesInfo.data.length > 0)

        this.stockUsageData.legend = {
          data: glassSizeList.map(seriesList => `${seriesList.height}x${seriesList.width}`)
        }

        let fullDateList = _.uniq(seriesList.map(seriesInfo => seriesInfo.data.map(dataPoint => moment(dataPoint.date).format('YYYY-MM-DD'))).flat())

        this.stockUsageData.xAxis = {
          type: 'time',
        }
        this.stockUsageData.yAxis = {
          type: 'value',
        }

        this.stockUsageData.options.tooltip = {
          trigger: 'axis',
          axisPointer: {
            label: {
              formatter: function (params) {
                return moment(params.value).format('DD MMM YY');
              }
            }
          }
        }
        
        this.stockUsageData.series = []

        seriesList.forEach(glassSizeSeries => {
          // Fill in all the dates so we don't have gaps
          let dateMap = {}
          fullDateList.forEach(date => {
            dateMap[date] = 0
          })
          glassSizeSeries.data.map(dataPoint => {
            dateMap[moment(dataPoint.date).format('YYYY-MM-DD')] = widgetComp.formatDecimal(dataPoint.totalUsage, 2, true)
          })

          this.stockUsageData.series.push({
            type: 'bar',
            stack: 'total',
            name: `${glassSizeSeries.height}x${glassSizeSeries.width}`,
            data: fullDateList.map(date => {
              return [date, dateMap[date]]
            })
          })
        })
      })
    },
  }
}
</script>